import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "sonner";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import Layout from "@/components/Layout";
import HomePage from "@/pages/HomePage";

// Keep only the homepage in the initial bundle. The remaining pages are fetched
// on navigation, so mobile visitors do not download admin/editor code up front.
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const CoursesPage = lazy(() => import("@/pages/CoursesPage"));
const CorporatePage = lazy(() => import("@/pages/CorporatePage"));
const CorporateTrackPage = lazy(() => import("@/pages/CorporateTrackPage"));
const CorporateUseCaseDetailPage = lazy(() => import("@/pages/CorporateUseCaseDetailPage"));
const GraduateTrainingPage = lazy(() => import("@/pages/GraduateTrainingPage"));
const WorkdayPracticeTestPage = lazy(() => import("@/pages/WorkdayPracticeTestPage"));
const SuccessStoriesPage = lazy(() => import("@/pages/SuccessStoriesPage"));
const LeadershipDetailPage = lazy(() => import("@/pages/LeadershipDetailPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const TalkToTrainingAdvisorPage = lazy(() => import("@/pages/TalkToTrainingAdvisorPage"));
const CourseDetailPage = lazy(() => import("@/pages/CourseDetailPage"));
const CourseRegisterRedirectPage = lazy(() => import("@/pages/CourseRegisterRedirectPage"));
const CourseLiveSessionRedirectPage = lazy(() => import("@/pages/CourseLiveSessionRedirectPage"));
const CourseEnrollRedirectPage = lazy(() => import("@/pages/CourseEnrollRedirectPage"));
const PaymentPage = lazy(() => import("@/pages/PaymentPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const ResourcesPage = lazy(() => import("@/pages/ResourcesPage"));
const ResourceDetailPage = lazy(() => import("@/pages/ResourceDetailPage"));
const LiveSessionsPage = lazy(() => import("@/pages/LiveSessionsPage"));
const LiveSessionDetailPage = lazy(() => import("@/pages/LiveSessionDetailPage"));
const CareersPage = lazy(() => import("@/pages/CareersPage"));
const CareerJobPage = lazy(() => import("@/pages/CareerJobPage"));
const BecomeTrainerPage = lazy(() => import("@/pages/BecomeTrainerPage"));
const AffiliatesPage = lazy(() => import("@/pages/AffiliatesPage"));
const AffiliateLoginPage = lazy(() => import("@/pages/AffiliateLoginPage"));
const AffiliateChangePasswordPage = lazy(() => import("@/pages/AffiliateChangePasswordPage"));
const AffiliatePortalPage = lazy(() => import("@/pages/AffiliatePortalPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AdminGate = lazy(() => import("@/components/AdminGate"));
const PolicyPage = lazy(() => import("@/pages/PolicyPage"));
const CertificatePage = lazy(() => import("@/pages/CertificatePage"));
const VerifyCertificatePage = lazy(() => import("@/pages/VerifyCertificatePage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const WhatsAppRedirectPage = lazy(() => import("@/pages/WhatsAppRedirectPage"));
const ExternalRedirectPage = lazy(() => import("@/pages/ExternalRedirectPage"));

function RouteFallback() {
  return <div className="min-h-[60vh]" aria-busy="true" aria-label="Loading page" />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Layout>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              {/* <Route path="//" element={<HomePage />} /> */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/about-us" element={<AboutPage />} />
              {/* Keep /home URL but render homepage content */}
              <Route path="/home" element={<HomePage />} />
              <Route path="/courses" element={<CoursesPage />} />
              <Route path="/course" element={<CoursesPage />} />
              <Route path="/courses/:slug" element={<CourseDetailPage />} />
              <Route path="/ai-corporate-courses/:slug" element={<CourseDetailPage source="ai-corporate" />} />
              <Route path="/courses/:slug/register-demo" element={<CourseRegisterRedirectPage />} />
              <Route path="/courses/:slug/register-session" element={<CourseLiveSessionRedirectPage />} />
              <Route path="/courses/:slug/enroll" element={<CourseEnrollRedirectPage />} />
              {/* Legacy marketing URL for this one course -- redirects to its
                  real course page. See frontend/public/.htaccess for the
                  matching server-side 301 search engines actually see. */}
              <Route path="/workday-hcm-training" element={<Navigate to="/courses/workday-hcm-mentorship" replace />} />
              {/* More legacy/retired course URLs -- see frontend/public/.htaccess
                  for the matching server-side 301s search engines actually see,
                  and prerender-routes.json's redirectRoutes for the SEO-
                  completeness test classification. One old course
                  (workday-financial-management, with a trailing slash + a
                  tracking query string) only needs the .htaccess rule, since
                  React Router already normalizes that to the real course page
                  at /courses/:slug -- adding a Route for it here would shadow
                  that real page instead of just cleaning up the stray URL. */}
              <Route path="/courses/git-and-github-training" element={<Navigate to="/courses" replace />} />
              <Route path="/courses/workday-testing" element={<Navigate to="/courses" replace />} />
              <Route path="/courses/ai-for-professionals" element={<Navigate to="/courses?category=ai" replace />} />
              <Route path="/courses/sap" element={<Navigate to="/courses?category=sap" replace />} />
              <Route path="/courses/sap-successfactors-onboarding-and-recruiting" element={<Navigate to="/courses" replace />} />
              <Route path="/courses/sap-joule-for-con=" element={<Navigate to="/courses?category=sap&q=Joule" replace />} />
              <Route path="/courses/sap-ai-for-mm" element={<Navigate to="/courses/sap-ai-for-mm-consultants" replace />} />
              <Route path="/quality-assurance-training" element={<Navigate to="/courses" replace />} />
              <Route path="/courses/sap-revenue-accounting-reporting-training" element={<Navigate to="/courses" replace />} />
              {/* Moved off-platform to Teachable -- external, so <Navigate> (SPA-only) can't be used. */}
              <Route path="/courses/sap-mdg-training" element={<ExternalRedirectPage to="https://zarantech.teachable.com/p/sap-mdg-9-2-training" />} />
              <Route path="/payment" element={<PaymentPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              {/* Old blog URLs from a past CMS/platform (WordPress media paths,
                  category/tag/author archives, pagination, etc.) have more path
                  segments than /blog/:slug matches, so they'd otherwise fall
                  through to the site's generic 404. Send all of those to the
                  blog listing instead -- /blog/:slug above still wins for an
                  actual single-segment post slug, since React Router ranks a
                  dynamic segment above a splat. */}
              <Route path="/blog/*" element={<Navigate to="/blog" replace />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/resources/:slug" element={<ResourceDetailPage />} />
              <Route path="/live-sessions" element={<LiveSessionsPage />} />
              <Route path="/live-sessions/:slug" element={<LiveSessionDetailPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/careers/:slug" element={<CareerJobPage />} />
              <Route path="/become-a-trainer" element={<BecomeTrainerPage />} />
              <Route path="/refer-a-friend" element={<AffiliatesPage />} />
              <Route path="/be-a-friend-tell-a-friend" element={<AffiliatesPage />} />
              <Route path="/affiliates" element={<Navigate to="/refer-a-friend" replace />} />
              <Route path="/refer-a-friend/login" element={<AffiliateLoginPage />} />
              <Route path="/refer-a-friend/change-password" element={<AffiliateChangePasswordPage />} />
              <Route path="/refer-a-friend/portal" element={<AffiliatePortalPage />} />
              <Route path="/affiliate/login" element={<Navigate to="/refer-a-friend/login" replace />} />
              <Route path="/affiliate/change-password" element={<Navigate to="/refer-a-friend/change-password" replace />} />
              <Route path="/affiliate/portal" element={<Navigate to="/refer-a-friend/portal" replace />} />
              <Route path="/corporate-training" element={<CorporatePage />} />
              <Route path="/corporate-training/sap" element={<CorporateTrackPage track="sap" />} />
              <Route path="/corporate-training/workday" element={<CorporateTrackPage track="workday" />} />
              <Route path="/corporate-training/ai" element={<CorporateTrackPage track="ai" />} />
              <Route path="/corporate-training/ai-leadership" element={<CorporateTrackPage track="ai-leadership" />} />
              <Route path="/corporate-training/case-studies/:slug" element={<CorporateUseCaseDetailPage />} />
              <Route path="/corporate-training/use-cases/:slug" element={<CorporateUseCaseDetailPage />} />
              <Route path="/corporate" element={<Navigate to="/corporate-training" replace />} />
              <Route path="/graduate-training" element={<GraduateTrainingPage />} />
              <Route path="/workday-practice-test" element={<WorkdayPracticeTestPage />} />
              <Route path="/success-stories" element={<SuccessStoriesPage />} />
              {/* Same AI for Leadership track content as /corporate-training/ai-leadership,
                  kept at its own URL (mirrors the /about + /about-us pattern above). */}
              <Route path="/leadership" element={<CorporateTrackPage track="ai-leadership" />} />
              <Route path="/leadership/:slug" element={<LeadershipDetailPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/talk-to-training-advisor" element={<TalkToTrainingAdvisorPage />} />
              <Route path="/contact-us" element={<ContactPage />} />
              <Route path="/admin" element={<AdminGate><AdminPage /></AdminGate>} />
              <Route path="/login" element={<AdminGate><AdminPage /></AdminGate>} />
              <Route path="/policies/:slug" element={<PolicyPage />} />
              <Route path="/certificate/:certificateId" element={<CertificatePage />} />
              <Route path="/verify-certificate" element={<VerifyCertificatePage />} />
              <Route path="/whatsapp" element={<WhatsAppRedirectPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </Layout>
        <Sonner position="bottom-right" richColors closeButton />
      </BrowserRouter>
    </div>
  );
}

export default App;
